import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import axios from 'axios';
import ItemDetail from './ItemDetail';
import { useFolder } from '../contexts/FolderContext';
import { filterAllowedItems } from '../utils/allowedItemsFilter';

// Hardcoded foodcourt name mapping (last fallback)
const hardcodedFcMapping = {
  '6051d1f64ec6952ca5b45dba': 'CG-Ecospace 6B-BLR',
  '60f172e2d1b6e328e744cf65': 'CG-Cyber Towers-CHN',
  '61e39f4b2acc405ba5c12202': 'CG-S82-NCR-NOI',
  '62720dcd1410b643ccc5eaf0': 'CG-DV B4-BLR',
  '6344e94b2db92732f3849cd4': 'S&P Global-Prahlad Nagar-AHD',
  '651ba1552d0b1d001b6c2e53': 'Air India-Santacruz-MUM',
  '65b48ebd8d7d8f001be02f79': 'Air India-Aviation Academy-GUR',
  '66751bbe191e5a001af454d4': 'Sodexo-CG GAR-HYD',
  '66751c8e3f015e001b2ed8b4': 'Sodexo-CG-Campus-HYD',
  '66ffbf790724f300133f7c22': 'CG Technopolis- Sec 5- KOL',
  '5ff01c4c6ed88b1ba8d68d36': 'Optum Manyata',
};

/**
 * Item Deep Dive tab: opens ItemDetail for an item selected from Restaurant Forecasts.
 * List UI was removed; this shell only loads folder data and hosts ItemDetail.
 */
function DeepDive({ initialSelectedItem, onBackToRestaurant, restaurantFilters, onItemUpdate }) {
  const [strictData, setStrictData] = useState([]);
  const [aggregatedData, setAggregatedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(initialSelectedItem || null);
  const { selectedFolder, refreshing } = useFolder();

  useEffect(() => {
    if (refreshing) {
      setStrictData([]);
      setAggregatedData([]);
      setError(null);
    }
  }, [refreshing]);

  useEffect(() => {
    if (initialSelectedItem) {
      setSelectedItem(initialSelectedItem);
    } else {
      setSelectedItem(null);
    }
  }, [initialSelectedItem]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [strictRes, aggregatedRes, fcRes, fcMappingRes] = await Promise.all([
        axios.get('/api/strict'),
        axios.get('/api/aggregated'),
        axios.get('/api/foodcourts').catch(() => ({ data: [] })),
        axios.get('/api/fc-mapping').catch(() => ({ data: [] })),
      ]);
      setStrictData(filterAllowedItems(strictRes.data || []));
      setAggregatedData(filterAllowedItems(aggregatedRes.data || []));

      // Keep FC mapping warm for ItemDetail/name resolution via parent flows
      const fcMap = {};
      (fcMappingRes.data || []).forEach((row) => {
        if (row.foodcourtid && row.foodcourtname) {
          fcMap[row.foodcourtid] = row.foodcourtname;
        }
      });
      (fcRes.data || []).forEach((fc) => {
        if (fc.id && fc.name && !fcMap[fc.id]) {
          fcMap[fc.id] = fc.name;
        }
      });
      Object.keys(hardcodedFcMapping).forEach((fcId) => {
        if (!fcMap[fcId]) fcMap[fcId] = hardcodedFcMapping[fcId];
      });

      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!refreshing) {
      loadData();
    }
  }, [selectedFolder, refreshing, loadData]);

  // Refresh selected item from newly loaded folder data
  useEffect(() => {
    if (!selectedItem || aggregatedData.length === 0 || refreshing || !selectedFolder) {
      return;
    }
    const updatedItem = aggregatedData.find(
      (row) =>
        row.foodcourtid === selectedItem.foodcourtid &&
        row.restaurant === selectedItem.restaurant &&
        row.itemname === selectedItem.itemname
    );
    if (updatedItem) {
      const itemChanged =
        updatedItem.avg_mape_component_model !== selectedItem.avg_mape_component_model ||
        updatedItem.status_component_model !== selectedItem.status_component_model;
      if (itemChanged) {
        setSelectedItem(updatedItem);
        if (onItemUpdate) onItemUpdate(updatedItem);
      }
    } else {
      setError('Item not found in the selected model date. Resetting to default view...');
      if (onItemUpdate) onItemUpdate(null);
    }
  }, [aggregatedData, selectedFolder, refreshing, selectedItem, onItemUpdate]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => {
      setError(null);
      if (error.includes('not found') || error.includes('Resetting')) {
        setSelectedItem(null);
        if (onItemUpdate) onItemUpdate(null);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [error, onItemUpdate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !selectedItem) {
    return <Alert severity="error">Error loading data: {error}</Alert>;
  }

  if (selectedItem) {
    const itemSource = initialSelectedItem ? 'restaurantlevel' : 'deepdive';
    const filtersToPass = itemSource === 'restaurantlevel' ? restaurantFilters : null;
    return (
      <Box>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <ItemDetail
          item={selectedItem}
          strictData={strictData}
          aggregatedData={aggregatedData}
          onBack={() => setSelectedItem(null)}
          onBackToRestaurant={onBackToRestaurant}
          source={itemSource}
          restaurantFilters={filtersToPass}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 8, textAlign: 'center' }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Item Level Deep Dive
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Select an item from Restaurant Forecasts to view detailed deep dive.
      </Typography>
    </Box>
  );
}

export default DeepDive;
